<script setup>
import { WdsPagination, WdsPopup, WdsRatingBreakdown, WdsReviewCard, WdsReviewCardReply, WdsReviewCardSkeleton, WdsSectionMessage, WdsSelectList } from '@wds/components';
import reviewsConfig from '@/config/reviews';

const props = defineProps({
  propertyId: {
    type: Number,
    required: true,
  },
  propertyName: {
    type: String,
    required: true,
  },
  propertyRating: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  propertyReviewsSummary: {
    type: String,
    required: false,
    default: '',
  },
  applySheet: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['closePropertyReviews']);

// COMPOSABLES
const { t } = useI18n();
const { useRating } = useRatings();
const { useUrl } = useUrls();
const { isBelowMediumScreen } = useMq();

// ### STORES
const testingStore = useTestingStore();

// ### A/B TESTING FEATURE TOGGLES
const experimentReviewsSummary = testingStore.hasEnabledFeature('web_enableAIReviewSummaryOnStaticPDP');

// ### API COMPOSABLES
const { getPropertyReviews } = useApiProperties();

// VARIABLE INITIALIZATION
const openLanguages = ref(false);
const openSort = ref(false);
const loading = ref(false);
const reviewDetailsBody = ref(null);
const showSortSheet = ref(false);
const showLanguageSheet = ref(false);

const filters = ref({
  sort: reviewsConfig.defaultSort,
  allLanguages: reviewsConfig.defaultReviewLanguage === 'all',
  page: 1,
  monthCount: reviewsConfig.reviewsMonthCount,
});
const pagination = ref([]);
const propertyReviews = ref([]);

const reviewGuidelinesUrl = useUrl.getReviewGuidelinesPageUrl();

await getReviewsData();

const ratingScoreTranslations = toValue(useRating?.getRatingScoreKeywordTranslations);

const ratingBreakdown = useRating?.getRatingBreakdown(props.propertyRating);

// COMPUTED
const sortOptions = computed(() => {
  return Object.entries(reviewsConfig.sort).map((sortOption) => {
    return {
      label: t(`t_${sortOption[0].toUpperCase()}`),
      value: sortOption[1],
    };
  });
});

const currentSort = computed(() => {
  return sortOptions.value.find((i) => i.value === filters.value.sort);
});

const allLanguagesOptions = computed(() => {
  return reviewsConfig.reviewsLanguage.map((languageOption) => {
    return {
      ...languageOption,
      label: t(languageOption.label),
    };
  });
});

const currentLanguageOption = computed(() => {
  return allLanguagesOptions.value.find((i) => i.value === (filters.value.allLanguages ? 'all' : 'specific'));
});

const totalPages = computed(() => {
  return pagination.value?.numberOfPages || 0;
});

// METHODS
async function getReviewsData() {
  loading.value = true;
  const reviewsObj = await getPropertyReviews(props.propertyId, filters.value);
  pagination.value = reviewsObj.pagination;
  propertyReviews.value = reviewsObj.reviews;
  loading.value = false;
  openSort.value = false;
  openLanguages.value = false;
}
function formatReviewData(review, detailed = false) {
  return {
    text: review?.reviewCard?.reviewText,
    stayedIn: `${t('t_STAYEDIN')} ${review?.reviewCard?.reviewDate}`,
    ratingScore: review?.overallRating,
    userInfo: {
      userName: review?.author?.nickname,
      description: `${review?.author?.gender}, ${review?.author?.ageRange}, ${review?.author?.countryName}`,
      flagURL: review?.author?.countryCode
        ? `https://a.hwstatic.com/hw/flags/${review.author.countryCode}.svg`
        : null,
    },
    showAsCard: !detailed,
    truncateText: !detailed,
    showReadMore: !detailed,
    translations: {
      tReadMore: t('t_SHOWMORE'),
      tReadLess: t('t_SHOWLESS'),
    },
    rank: detailed && !isBelowMediumScreen ? t(review?.author?.rank) : null,
    totalReviews: detailed && !isBelowMediumScreen ? Number(review?.author?.numberOfReviews) : null,
  };
}
function setSort(option) {
  showSortSheet.value = false;

  if (filters.value.sort !== option.value) {
    filters.value.sort = option.value;
    filters.value.page = 1;
  }
}

function setLanguage(option) {
  const selection = option.value === 'all';
  showLanguageSheet.value = false;

  if (filters.value.allLanguages !== selection) {
    filters.value.allLanguages = selection;
    filters.value.page = 1;
  }
}

function reviewReplyData(review) {
  // NOTE: lack of the data from the api response for the review reply date
  return {
    size: 'large',
    propertyInfo: {
      userName: props.propertyName,
      description: 'Response from property',
      icon: 'hostel',
    },
    text: review?.reviewCard?.ownerComment,
    replyDate: '',
    loading: false,
  };
}

function loadPage(pagination) {
  reviewDetailsBody.value.scrollTo(0, 0);
  filters.value.page = pagination;
}

function closePropertyReviewsWrapper() {
  emit('closePropertyReviews', 'reviews');
}

watch(
  filters,
  () => {
    getReviewsData();
  },
  { deep: true },
);
</script>

<template>
  <WdsPopup
    is-open
    :apply-sheet="applySheet"
    :heading="$t('t_REVIEWS')"
    close-button
    @popup-close="closePropertyReviewsWrapper"
  >
    <template #popupContent>
      <WdsSectionMessage
        class="review-details-section-message"
        icon="communication"
        :title="$t('t_REVIEWBLURB2')"
        type="customizable"
      />
      <div class="review-and-ratings-container">
        <div class="ratings-details">
          <div class="ratings-details-breakdown-wrapper">
            <WdsRatingBreakdown
              :city-info="ratingBreakdown"
              :translations="ratingScoreTranslations"
              :rating-translations="ratingScoreTranslations"
              :card-layout="false"
              :enable-collapse="isBelowMediumScreen"
            />
            <a
              :href="reviewGuidelinesUrl"
              class="guidelines-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('t_REVIEWSGUIDELINES') }}
            </a>
          </div>
          <PropertyHwReviewsSummary
            v-if="experimentReviewsSummary"
            class="ratings-details-reviews-summary"
            :property-id="propertyId"
            :property-reviews-summary="propertyReviewsSummary"
          />
        </div>
        <div class="review-details">
          <div class="review-details-header">
            <div class="pills">
              <CommonHwPillMenu
                :is-menu-open="openSort"
                icon-end="caret-down"
                :text="currentSort.label"
                :aria-label="$t('t_SORTBY')"
                @pill-menu-click="openSort = !openSort"
                @pill-menu-click-outside="openSort = false"
              >
                <template #menu>
                  <WdsSelectList
                    class="menu-slot"
                    :aria-label="$t('t_SORTBY')"
                    :items="sortOptions"
                    :selected="currentSort.value"
                    decorated
                    @item-selected="setSort"
                  />
                </template>
              </CommonHwPillMenu>
              <CommonHwPillMenu
                :is-menu-open="openLanguages"
                icon-end="caret-down"
                :text="currentLanguageOption.label"
                :aria-label="$t('t_LANGUAGE')"
                @pill-menu-click="openLanguages = !openLanguages"
                @pill-menu-click-outside="openLanguages = false"
              >
                <template #menu>
                  <WdsSelectList
                    class="menu-slot"
                    :aria-label="$t('t_LANGUAGE')"
                    :items="allLanguagesOptions"
                    :selected="currentLanguageOption.value"
                    decorated
                    @item-selected="setLanguage"
                  />
                </template>
              </CommonHwPillMenu>
            </div>
          </div>
          <div
            ref="reviewDetailsBody"
            class="review-details-body"
          >
            <template v-if="loading">
              <div class="review-skeleton">
                <WdsReviewCardSkeleton
                  v-for="n in 3"
                  :key="n"
                  :loading="loading"
                  class="comment"
                />
              </div>
            </template>
            <template
              v-for="(review, index) in propertyReviews"
              v-else
              :key="`review-${index}`"
            >
              <div class="review">
                <WdsReviewCard
                  v-bind="formatReviewData(review, true)"
                  class="comment"
                />
                <template v-if="review?.reviewCard?.ownerComment">
                  <WdsReviewCardReply
                    :key="`reply-${index}`"
                    v-bind="reviewReplyData(review)"
                    class="reply"
                  />
                </template>
              </div>
            </template>
            <div class="pagination">
              <WdsPagination
                v-if="!loading"
                :starting-page="filters.page"
                :num-pages="totalPages"
                @page-move="loadPage"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </WdsPopup>
</template>

<style lang="scss" scoped>
:deep(.modal-container) {
  @include tablet-large {
    max-height: calc(100% - 6rem) !important;
  }

  .modal-body {
    display: flex;
    flex-direction: column;
  }
}

.review-details-section-message {
  border-radius: $wds-border_radius-xl;
  justify-content: center;
  align-items: center;
  background-color: $wds-color-sunset-lighter !important;
  box-shadow: none !important;
  margin-bottom: $wds-spacing-m;

  &:deep(.item-text) {
    margin-right: unset;
  }

  &:deep(.icon) {
    height: auto;
  }
}

.review-and-ratings-container {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-l;

  @include tablet-large {
    display: grid;
    grid-template-areas: 'sidebar body';
    grid-template-rows: auto 1fr;
    column-gap: $wds-spacing-xxl;
    max-height: calc(100% - 15rem);
    overflow: hidden;
  }

  .ratings-details {
    display: flex;
    flex-direction: column;
    gap: $wds-spacing-m;

    .ratings-details-breakdown-wrapper {
      & :deep(.rating-breakdown-component) {
        @include tablet-large {
          width: 19rem;
        }
      }

      .guidelines-link {
        @include link-2-reg;

        display: block;
        color: $wds-color-ink-darker !important;
        text-decoration: underline;
      }
    }

    @include tablet-large {
      grid-area: sidebar;
      width: min-content;
      height: 100%;
      overflow-y: auto;
      scrollbar-gutter: stable;

      .ratings-details-breakdown-wrapper {
        box-shadow: inset 0 0 0 1px $wds-color-ink-lighter;
        border-radius: $wds-border_radius-xl;
        padding: $wds-spacing-m;

        .guidelines-link {
          margin-top: $wds-spacing-m;
        }
      }
    }
  }

  .review-details {
    grid-area: body;
    overflow: hidden;

    .review-details-header {
      border-bottom: 1px solid $wds-color-ink-lighter;
      padding-bottom: $wds-spacing-m;

      .title {
        @include title-6-bld;

        margin-bottom: $wds-spacing-m;
      }

      .pills {
        display: flex;
        gap: $wds-spacing-s;

        .menu-slot {
          position: absolute;
          width: fit-content;
          max-width: 15rem;
          margin-top: $wds-spacing-xs;
          z-index: $wds-z-index-dropdown;
        }
      }
    }

    .review-details-body {
      overflow-y: auto;
      scrollbar-gutter: stable;
      height: calc(100% - 5rem);

      .review {
        border-bottom: 1px solid $wds-color-ink-lighter;
        padding: $wds-spacing-m $wds-spacing-s;

        @include tablet-large {
          margin: 0 $wds-spacing-m;
          padding: $wds-spacing-m 0;
          max-width: wds-rem(454px);
        }
      }

      .review-skeleton {
        border-bottom: 1px solid $wds-color-ink-lighter;
        margin: 0 $wds-spacing-m;
        padding: $wds-spacing-m 0;

        @include tablet-large {
          width: wds-rem(420px);
        }
      }

      .comment {
        padding-bottom: $wds-spacing-m;

        &:deep(.review-card-container .review-card-userinfo .review-card-title-container) {
          text-align: right;
        }
      }

      .reply {
        margin: $wds-spacing-m 0 $wds-spacing-l $wds-spacing-l;
      }

      .pagination {
        display: flex;
        justify-content: center;
        padding-bottom: $wds-spacing-m;
        padding-top: $wds-spacing-m;

        @include tablet-large {
          min-height: wds-rem(72px);
        }
      }
    }
  }
}
</style>
